import api from './api';

export interface AzureAuthStatus {
  authenticated: boolean;
  expires_in?: number;
  calendar_source?: 'configured_users' | 'signed_in_user';
  calendar_users?: string[];
  availability_mode?: 'any' | 'all';
}

export interface AzureAuthResponse {
  message: string;
  access_token: string;
  expires_in: number;
}

const azureService = {
  // Get Azure OAuth URL
  getAuthUrl: () => api.get('/azure/auth'),

  // Handle OAuth callback
  handleCallback: (code: string, state: string) =>
    api.post('/azure/callback', { code, state }),

  // Refresh access token
  refreshToken: () => api.post('/azure/refresh'),

  // Check authentication status
  checkAuth: () => api.get('/azure/check-auth'),

  // Logout from Azure
  logout: () => api.post('/azure/logout'),

  // Initiate OAuth flow
  initiateAuth: async () => {
    try {
      const response = await api.get('/azure/auth');
      const authUrl = response.data.auth_url;

      console.log('Opening Azure OAuth URL:', authUrl);

      // Open Azure OAuth in a popup window
      const popup = window.open(
        authUrl,
        'azure-oauth',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      return new Promise<AzureAuthResponse>((resolve, reject) => {
        // Listen for messages from the popup
        const messageListener = (event: MessageEvent) => {
          console.log('Received message from popup:', event.data);

          // New backend format: { azureAuth: 'success', session_key }
          if (
            event.data &&
            event.data.azureAuth === 'success' &&
            event.data.session_key
          ) {
            window.removeEventListener('message', messageListener);
            popup?.close();
            // Associate the token with the authenticated user
            api
              .post('/azure/associate-token', {
                session_key: event.data.session_key,
              })
              .then(response => {
                console.log('Token association successful:', response.data);
                resolve(response.data);
              })
              .catch((error: any) => {
                console.error('Token association failed:', error);
                reject(
                  new Error(
                    'Failed to associate token: ' +
                      (error.response?.data?.error || error.message)
                  )
                );
              });
          } else if (event.data && event.data.azureAuth === 'error') {
            window.removeEventListener('message', messageListener);
            popup?.close();
            reject(
              new Error(event.data.error || 'Azure authentication failed')
            );
          }
        };

        window.addEventListener('message', messageListener);

        // Check if popup was closed
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageListener);
            reject(new Error('Authentication was cancelled'));
          }
        }, 1000);
      });
    } catch (error) {
      console.error('Azure OAuth initiation error:', error);
      throw error;
    }
  },

  // Check if user is authenticated with Azure
  isAuthenticated: async (): Promise<AzureAuthStatus> => {
    try {
      console.log('Checking Azure authentication via API...');
      const response = await api.get('/azure/check-auth');
      console.log('Azure auth check response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Azure auth check failed:', error);
      return { authenticated: false };
    }
  },

  // Get Azure access token from backend (auto-refreshes if needed)
  getToken: async (): Promise<{ access_token: string; expires_in: number }> => {
    try {
      const response = await api.get('/azure/token');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || 'Failed to get Azure token'
      );
    }
  },
};

export default azureService;
